import React, { useState } from 'react';
import { Row, Col, message } from 'antd';
import { Button, Modal } from 'ui-kit';
import PropTypes from 'prop-types';
import { SOCIAL_PROVIDERS } from 'consts';
import SignInOption from './SignInOption';
import ChangePasswordForm from './ChangePasswordForm';
import { disconnectSocialProvider, socialLoginOrCreateOwner } from 'api/auth';

export default function SingInOptions({ socialAuths, hasSetPassword, onChangeSettings }) {
  const [disconnecting, setDisconnecting] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [disconnectProvider, setDisconnectProvider] = useState(null);
  const isModalOpen = disconnectProvider !== null;
  const socialProviderName = SOCIAL_PROVIDERS[disconnectProvider?.backend];

  const handleSocialAuth = (connected, providerId) => {
    if (connected) {
      const { id: associationId, provider: backend } = socialAuths.find(
        ({ provider }) => provider === providerId,
      );
      setDisconnectProvider({ associationId, backend });
    } else {
      socialLoginOrCreateOwner(providerId, { next: window.location.href });
    }
  };

  const handleEmailAuth = () => {
    setChangePassword((prev) => !prev);
  };

  const cleanDisconnectProvider = () => {
    setDisconnectProvider(null);
  };

  const onDisconnectProvider = () => {
    setDisconnecting(true);
    disconnectSocialProvider(disconnectProvider).then((res) => {
      setDisconnecting(false);
      if (res.ok) {
        message.success(`${socialProviderName} disconnected`);
        onChangeSettings();
        cleanDisconnectProvider();
      } else {
        message.error(`Error disconnecting ${socialProviderName}`);
      }
    });
  };

  const enabledToDisconnect = React.useMemo(() => {
    const providers = socialAuths.map(({ provider }) => provider);
    const oauthProviders = providers.filter((id) => Boolean(SOCIAL_PROVIDERS[id]));
    return hasSetPassword || oauthProviders.length > 1;
  }, [JSON.stringify(socialAuths), hasSetPassword]);

  return (
    <>
      {Object.entries(SOCIAL_PROVIDERS).map(([providerId, name]) => {
        const connected = socialAuths.findIndex(({ provider }) => provider === providerId) !== -1;
        return (
          <SignInOption
            key={providerId}
            connected={connected}
            disabled={connected && !enabledToDisconnect}
            label={name}
            onClick={() => handleSocialAuth(connected, providerId)}
          />
        );
      })}
      <SignInOption
        connected={hasSetPassword}
        label="Email"
        actionLabel={hasSetPassword ? 'Change password' : 'Add'}
        onClick={handleEmailAuth}
      />
      {changePassword && (
        <ChangePasswordForm onSubmit={onChangeSettings} hasSetPassword={hasSetPassword} />
      )}
      <Modal
        className="hc-settings__modal"
        title={`Disconnect  ${socialProviderName}`}
        visible={isModalOpen}
        onCancel={cleanDisconnectProvider}
        maskStyle={{
          background: 'rgba(245, 238, 249, 0.5)',
          backdropFilter: 'blur(15px)',
        }}
        footer={
          <div className="Modal_footer">
            <Row gutter={[0, 16]}>
              <Col xs={24}>
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  onClick={onDisconnectProvider}
                  loading={disconnecting}
                >
                  {`Disconnect  ${socialProviderName}`}
                </Button>
              </Col>
              <Col xs={24}>
                <Button fullWidth onClick={cleanDisconnectProvider}>
                  Cancel and take me back
                </Button>
              </Col>
            </Row>
          </div>
        }
      >
        <Row>
          <Col>
            <p>
              {`If you continue, you'll no longer be able to sign in using ${socialProviderName}.`}
            </p>
            <br />
            <p>{`(You can re-enable ${socialProviderName} later.)`}</p>
          </Col>
        </Row>
      </Modal>
    </>
  );
}

SingInOptions.propTypes = {
  socialAuths: PropTypes.arrayOf(
    PropTypes.shape({
      provider: PropTypes.string,
      id: PropTypes.number,
    }),
  ),
  hasSetPassword: PropTypes.bool,
  onChangeSettings: PropTypes.func,
};
